
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  watch,
  PropType,
  onMounted,
  inject,
} from "vue";
import FilePreview from "@/components/parts/FilePreview.vue";
import FileUpload from "@/components/parts/FileUpload.vue";
import CustomDatepicker from "@/components/atomics/CustomDatepicker.vue";
import Dropdown from "@/components/atomics/Dropdown.vue";
import ValidationTextField from "@/components/atomics/ValidationTextField.vue";
import TextButton from "@/components/atomics/TextButton.vue";
import { TemplateInformation as ContractDetailInfo } from "@/models/contracts/Information";
import { FileInfo } from "@/models/contracts/FileInfo";

import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { getFileExtension } from "@/lib/utility/common";
import router from "@/router";
import { useRoute } from "vue-router";

interface ContractBasicInfo {
  title: string;
  contractPartnerName: string;
}

export default defineComponent({
  name: "TemplateFileAndInfo",
  components: {
    FileUpload,
    FilePreview,
    CustomDatepicker,
    Dropdown,
    ValidationTextField,
    TextButton,
  },
  emits: [
    "update:is-docx-template",
    "update:contractBasicInfo",
    "update:contractDetailInfo",
    "update:files",
    "update:contractCustomFields",
  ],
  props: {
    files: {
      type: Array as PropType<Array<FileInfo>>,
      required: true,
    },
    contractBasicInfo: {
      type: Object as PropType<ContractBasicInfo>,
      required: true,
    },
    contractDetailInfo: {
      type: Object as PropType<ContractDetailInfo>,
      required: true,
    },
    contractCustomFields: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const i18n = useI18n();
    const isMobile = inject("isMobile");
    const state = reactive({
      selectedIndex: 0,
      isShowAdvanced: true,
      isEdit: false,
      customFields: [] as any,
    });

    const store = useStore();
    const route = useRoute();

    const automaticOptions = [
      {
        id: "true",
        name: i18n.t("template.updateOption1"),
      },
      {
        id: "false",
        name: i18n.t("template.updateOption2"),
      },
    ];

    const isDocxTemplate = computed(() =>
      props.files.some(
        (file: any) =>
          getFileExtension(file?.name) == "docx" ||
          getFileExtension(file?.name) == "doc"
      )
    );

    const fileType = computed(() => {
      // if (props.files.length == 0) return ".docx,.doc,pdf";
      // if (isDocxTemplate.value) return ".docx,.doc,pdf";
      return "pdf";
    });

    watch(isDocxTemplate, (value) => {
      if (value) emit("update:is-docx-template", true);
      else emit("update:is-docx-template", false);
    });

    const localContractBasicInfo = computed({
      get(): ContractBasicInfo {
        return props.contractBasicInfo;
      },

      set(value: ContractBasicInfo) {
        emit("update:contractBasicInfo", value);
      },
    });

    const localContractDetailInfo = computed({
      get(): ContractDetailInfo {
        return props.contractDetailInfo;
      },

      set(value: ContractDetailInfo) {
        emit("update:contractDetailInfo", value);
      },
    });

    const localFiles = computed({
      get(): FileInfo[] {
        return props.files;
      },

      set(value: FileInfo[]) {
        emit("update:files", value);
      },
    });

    const localCustomFields = computed({
      get(): any[] {
        return props.contractCustomFields;
      },

      set(value: any[]) {
        emit("update:contractCustomFields", value);
      },
    });

    const initialContractName = (name: string) => {
      if (!state.isEdit) {
        localContractBasicInfo.value.title = name.slice(0, -4);
      }
    };

    
    const addCustomField = () => {
      localCustomFields.value.push({
        title: "",
        value: "",
      });
    };

    const removeCustomField = (index: number) => {
      if (localCustomFields.value.length > 0) {
        localCustomFields.value.splice(index, 1);
      }
    };

    onMounted(async () => {
      state.isEdit = route.query.isEdit === "true";
    });

    return {
      ...toRefs(state),
      automaticOptions,
      localFiles,
      localContractBasicInfo,
      localContractDetailInfo,
      localCustomFields,
      isDocxTemplate,
      fileType,
      initialContractName,
      addCustomField,
      removeCustomField
    };
  },
});
